/**
 * AIQueryEngine - Natural Language Query Processing
 * TASK-206 Phase 3: AI Chat Assistant
 *
 * Processes natural language queries about portfolio data and generates
 * intelligent responses with UI update commands
 */

import { ClientLevelData } from '../hooks/useClientLevelData';

export type QueryIntent =
  | 'FILTER_BY_POSITION'
  | 'COMPARE_PORTFOLIOS'
  | 'SHOW_METRICS'
  | 'ANALYZE_SCENARIO'
  | 'FIND_OPPORTUNITIES'
  | 'SHOW_RISKS'
  | 'UNKNOWN';

export interface QueryResult {
  intent: QueryIntent;
  response: string;
  uiCommands?: UICommand[];
  data?: any;
  suggestedQuestions?: string[];
}

export interface UICommand {
  type: 'SWITCH_TAB' | 'FILTER_POSITIONS' | 'HIGHLIGHT_SECTION' | 'SELECT_PORTFOLIOS' | 'SCROLL_TO';
  payload: any;
}

export class AIQueryEngine {
  private clientData: ClientLevelData;

  constructor(clientData: ClientLevelData) {
    this.clientData = clientData;
  }

  /**
   * Process a natural language query
   */
  async processQuery(query: string): Promise<QueryResult> {
    const lowerQuery = query.toLowerCase().trim();

    // Detect intent based on keywords
    const intent = this.detectIntent(lowerQuery);

    switch (intent) {
      case 'FILTER_BY_POSITION':
        return this.handleFilterByPosition(lowerQuery);

      case 'COMPARE_PORTFOLIOS':
        return this.handleComparePortfolios(lowerQuery);

      case 'SHOW_METRICS':
        return this.handleShowMetrics(lowerQuery);

      case 'ANALYZE_SCENARIO':
        return this.handleAnalyzeScenario(lowerQuery);

      case 'FIND_OPPORTUNITIES':
        return this.handleFindOpportunities(lowerQuery);

      case 'SHOW_RISKS':
        return this.handleShowRisks(lowerQuery);

      default:
        return this.handleUnknown(lowerQuery);
    }
  }

  /**
   * Detect query intent from natural language
   */
  private detectIntent(query: string): QueryIntent {
    // Filter by position
    if (query.includes('show') && (query.includes('portfolio') || query.includes('portfolios')) &&
        (query.includes('have') || query.includes('has') || query.includes('with'))) {
      return 'FILTER_BY_POSITION';
    }

    // Compare portfolios
    if (query.includes('compare') || (query.includes('difference') && query.includes('between'))) {
      return 'COMPARE_PORTFOLIOS';
    }

    // Show metrics
    if ((query.includes('what') || query.includes('show')) &&
        (query.includes('exposure') || query.includes('risk') || query.includes('usd') ||
         query.includes('currency') || query.includes('fx'))) {
      return 'SHOW_METRICS';
    }

    // Analyze scenario (what-if)
    if (query.includes('what if') || query.includes('if i sold') || query.includes('if i bought')) {
      return 'ANALYZE_SCENARIO';
    }

    // Find opportunities
    if (query.includes('find') || query.includes('opportunity') || query.includes('opportunities') ||
        query.includes('tax harvest') || query.includes('optimize')) {
      return 'FIND_OPPORTUNITIES';
    }

    // Show risks
    if (query.includes('risk') || query.includes('concentration') || query.includes('top')) {
      return 'SHOW_RISKS';
    }

    return 'UNKNOWN';
  }

  /**
   * Handle: "Show me all portfolios that have Apple"
   */
  private handleFilterByPosition(query: string): QueryResult {
    // Extract security name (simple pattern matching)
    const securityName = this.extractSecurityName(query);

    if (!securityName) {
      return {
        intent: 'FILTER_BY_POSITION',
        response: "I couldn't identify which security you're looking for. Could you please specify the security name?",
        suggestedQuestions: [
          "Show me all portfolios that have Apple",
          "Which portfolios hold Nestlé?",
          "Show positions with MMGR Fund"
        ]
      };
    }

    // Find positions matching the security
    const matchingPositions = this.clientData.consolidatedHoldings.positions.filter(pos =>
      pos.name.toLowerCase().includes(securityName.toLowerCase())
    );

    if (matchingPositions.length === 0) {
      return {
        intent: 'FILTER_BY_POSITION',
        response: `I couldn't find any positions matching "${securityName}" in your portfolios.`,
        suggestedQuestions: [
          `Do you mean ${this.clientData.consolidatedHoldings.positions[0]?.name}?`
        ]
      };
    }

    const position = matchingPositions[0];
    const portfolioList = position.portfolios.join(', ');

    return {
      intent: 'FILTER_BY_POSITION',
      response: `**${position.name}** is held in ${position.portfolios.length} ${position.portfolios.length === 1 ? 'portfolio' : 'portfolios'}: ${portfolioList}.\n\n**Total Value:** CHF ${(position.value / 1000).toFixed(0)}K (${position.percentOfTotal.toFixed(1)}% of total wealth)\n\n**Performance:** ${position.unrealized_pl >= 0 ? '+' : ''}${(position.unrealized_pl / 1000).toFixed(0)}K (${position.unrealized_pl_pct >= 0 ? '+' : ''}${position.unrealized_pl_pct.toFixed(1)}%)\n\nI've filtered the Composition tab to show only ${position.name} positions.`,
      uiCommands: [
        { type: 'SWITCH_TAB', payload: 'composition' },
        { type: 'FILTER_POSITIONS', payload: { isin: position.isin } }
      ],
      data: { position },
      suggestedQuestions: [
        "What if I sold all MMGR?",
        "Show my USD exposure",
        "Find tax harvesting opportunities"
      ]
    };
  }

  /**
   * Handle: "Compare Conservative and Aggressive portfolios"
   */
  private handleComparePortfolios(query: string): QueryResult {
    // Extract portfolio names
    // const portfolioKeywords = this.clientData.portfolios.map(p =>
    //   p.name.toLowerCase().split(' ')
    // ).flat();

    const selectedPortfolios = this.clientData.portfolios.filter(p => {
      const nameParts = p.name.toLowerCase().split(' ');
      return nameParts.some(part => query.includes(part));
    });

    if (selectedPortfolios.length < 2) {
      return {
        intent: 'COMPARE_PORTFOLIOS',
        response: "I need at least 2 portfolios to compare. Which portfolios would you like to compare?",
        suggestedQuestions: this.clientData.portfolios.map(p => `Compare ${p.name}`)
      };
    }

    const portfolio1 = selectedPortfolios[0];
    const portfolio2 = selectedPortfolios[1];

    const perf1 = portfolio1.performance ?? 0;
    const perf2 = portfolio2.performance ?? 0;
    const perfDiff = perf1 - perf2;

    return {
      intent: 'COMPARE_PORTFOLIOS',
      response: `Comparing **${portfolio1.name}** vs **${portfolio2.name}**:\n\n**Values:**\n• ${portfolio1.name}: CHF ${(portfolio1.value / 1000000).toFixed(2)}M (${portfolio1.percentOfClientAuM.toFixed(1)}% of AuM)\n• ${portfolio2.name}: CHF ${(portfolio2.value / 1000000).toFixed(2)}M (${portfolio2.percentOfClientAuM.toFixed(1)}% of AuM)\n\n**Performance (YTD):**\n• ${portfolio1.name}: ${perf1 >= 0 ? '+' : ''}${perf1.toFixed(1)}%\n• ${portfolio2.name}: ${perf2 >= 0 ? '+' : ''}${perf2.toFixed(1)}%\n• Difference: ${perfDiff >= 0 ? '+' : ''}${perfDiff.toFixed(1)}%\n\n**Risk Profiles:**\n• ${portfolio1.name}: ${portfolio1.riskProfile || 'N/A'}\n• ${portfolio2.name}: ${portfolio2.riskProfile || 'N/A'}\n\nI've switched to the Portfolios tab to show a detailed comparison.`,
      uiCommands: [
        { type: 'SWITCH_TAB', payload: 'portfolios' },
        { type: 'SELECT_PORTFOLIOS', payload: selectedPortfolios.map(p => p.id) }
      ],
      data: { portfolios: selectedPortfolios },
      suggestedQuestions: [
        "What are the overlapping positions?",
        "Show allocation differences",
        "Which has better risk-adjusted returns?"
      ]
    };
  }

  /**
   * Handle: "What's my USD exposure and FX risk?"
   */
  private handleShowMetrics(query: string): QueryResult {
    const isCurrencyQuery = query.includes('usd') || query.includes('currency') || query.includes('fx');

    if (isCurrencyQuery && this.clientData.insights.currencyRisk) {
      const usdExposure = this.clientData.insights.currencyRisk.usdExposure ?? 0;
      const fxImpact = this.clientData.insights.currencyRisk.fxImpactScenario?.usdChfDrop10Pct ?? 0;
      const currentHedge = this.clientData.insights.currencyRisk.fxImpactScenario?.currentHedge ?? 0;
      const recommendation = this.clientData.insights.currencyRisk.fxImpactScenario?.recommendation ?? 'Consider reviewing currency exposure';

      return {
        intent: 'SHOW_METRICS',
        response: `**USD Currency Exposure Analysis:**\n\n**Current USD Exposure:** ${usdExposure.toFixed(1)}% of total portfolio (CHF ${(this.clientData.client.totalAuM * usdExposure / 100 / 1000).toFixed(0)}K)\n\n**FX Risk Scenario:**\nIf USD/CHF drops 10% → Portfolio impact: ${fxImpact.toFixed(1)}% (CHF ${(this.clientData.client.totalAuM * Math.abs(fxImpact) / 100 / 1000).toFixed(0)}K loss)\n\n**Current Hedge:** ${currentHedge.toFixed(0)}%\n\n**Recommendation:** ${recommendation}\n\nI've highlighted the Currency Risk section in the Overview tab.`,
        uiCommands: [
          { type: 'SWITCH_TAB', payload: 'overview' },
          { type: 'HIGHLIGHT_SECTION', payload: 'currency-risk' }
        ],
        data: { currencyRisk: this.clientData.insights.currencyRisk },
        suggestedQuestions: [
          "What if I hedged 50% of USD exposure?",
          "Show all USD positions",
          "Compare USD vs CHF allocation"
        ]
      };
    }

    // Default metrics summary
    return {
      intent: 'SHOW_METRICS',
      response: `**Portfolio Metrics Summary:**\n\n**Total AuM:** CHF ${(this.clientData.client.totalAuM / 1000000).toFixed(2)}M\n**Performance (YTD):** ${(this.clientData.performance.combined.ytd ?? 0) >= 0 ? '+' : ''}${(this.clientData.performance.combined.ytd ?? 0).toFixed(1)}%\n**Risk (Volatility):** ${(this.clientData.risk.combined.volatility ?? 0).toFixed(1)}%\n**Sharpe Ratio:** ${(this.clientData.risk.combined.sharpeRatio ?? 0).toFixed(2)}\n**Health Score:** ${this.clientData.client.healthScore ?? 'N/A'}/100\n\nWhat specific metric would you like to explore?`,
      suggestedQuestions: [
        "Show USD exposure",
        "What are the top risks?",
        "Show performance by portfolio"
      ]
    };
  }

  /**
   * Handle: "What if I sold all MMGR?"
   */
  private handleAnalyzeScenario(query: string): QueryResult {
    const securityName = this.extractSecurityName(query);

    if (!securityName) {
      return {
        intent: 'ANALYZE_SCENARIO',
        response: "Which security would you like to analyze?",
        suggestedQuestions: [
          "What if I sold all MMGR?",
          "What if I sold half my Apple position?",
          "What if I bought more Nestlé?"
        ]
      };
    }

    // Find the position
    const position = this.clientData.consolidatedHoldings.positions.find(p =>
      p.name.toLowerCase().includes(securityName.toLowerCase())
    );

    if (!position) {
      return {
        intent: 'ANALYZE_SCENARIO',
        response: `I couldn't find a position for "${securityName}".`,
      };
    }

    // Calculate impact
    const currentValue = position.value;
    const percentOfTotal = position.percentOfTotal;
    const unrealizedGain = position.unrealized_pl;
    const taxImpact = unrealizedGain > 0 ? unrealizedGain * 0.25 : 0; // Assume 25% tax rate

    const isSellScenario = query.includes('sold') || query.includes('sell');

    if (isSellScenario) {
      return {
        intent: 'ANALYZE_SCENARIO',
        response: `**Scenario Analysis: Selling ${position.name}**\n\n**Current Position:**\n• Value: CHF ${(currentValue / 1000).toFixed(0)}K (${percentOfTotal.toFixed(1)}% of total)\n• Unrealized P/L: ${unrealizedGain >= 0 ? '+' : ''}CHF ${(unrealizedGain / 1000).toFixed(0)}K\n\n**If You Sell:**\n• Cash proceeds: CHF ${(currentValue / 1000).toFixed(0)}K\n• Tax on gains: ~CHF ${(taxImpact / 1000).toFixed(0)}K (estimated)\n• Net after-tax: CHF ${((currentValue - taxImpact) / 1000).toFixed(0)}K\n• Concentration risk: Would reduce from ${percentOfTotal.toFixed(1)}% to 0%\n\n**Portfolio Impact:**\n• Total AuM: CHF ${(this.clientData.client.totalAuM / 1000000).toFixed(2)}M → CHF ${((this.clientData.client.totalAuM - currentValue) / 1000000).toFixed(2)}M\n• Cash allocation: ${this.clientData.consolidatedHoldings.assetAllocation.Cash}% → ${(this.clientData.consolidatedHoldings.assetAllocation.Cash + percentOfTotal).toFixed(1)}%\n\n${percentOfTotal > 10 ? '**Recommendation:** Consider spreading the sale over multiple tax years to minimize tax impact.' : '**Recommendation:** Position is small enough to sell in one transaction.'}`,
        data: { position, scenario: 'sell' },
        suggestedQuestions: [
          "Find tax loss harvesting opportunities",
          "What should I do with the proceeds?",
          "Show optimization suggestions"
        ]
      };
    }

    return {
      intent: 'ANALYZE_SCENARIO',
      response: `I can analyze various scenarios for ${position.name}. Would you like to know what happens if you sell, buy more, or rebalance?`,
      suggestedQuestions: [
        `What if I sold all ${position.name}?`,
        `What if I doubled my ${position.name} position?`,
        `What's the risk of holding ${position.name}?`
      ]
    };
  }

  /**
   * Handle: "Find tax harvesting opportunities"
   */
  private handleFindOpportunities(query: string): QueryResult {
    const isTaxQuery = query.includes('tax') || query.includes('harvest');
    const isOptimizationQuery = query.includes('optimize') || query.includes('optimization');

    if (isTaxQuery) {
      const clientDataAny = this.clientData as any;
      const taxOps = clientDataAny.taxEfficiency?.taxLossOpportunities || [];
      const totalSavings = clientDataAny.taxEfficiency?.estimatedTotalTaxSavings || 0;

      if (taxOps.length === 0) {
        return {
          intent: 'FIND_OPPORTUNITIES',
          response: "**No Tax Loss Harvesting Opportunities Found**\n\nAll positions currently have unrealized gains. Tax loss harvesting is only possible when you have positions with losses.\n\nConsider checking again after market volatility or rebalancing.",
          suggestedQuestions: [
            "Find optimization opportunities",
            "Show gain coordination strategies",
            "What are my top risks?"
          ]
        };
      }

      const oppList = taxOps.map((opp: any, idx: number) =>
        `${idx + 1}. **${opp.security}**: ${opp.lossPercentage.toFixed(1)}% loss → Save CHF ${(opp.potentialTaxSavings / 1000).toFixed(1)}K`
      ).join('\n');

      return {
        intent: 'FIND_OPPORTUNITIES',
        response: `**Tax Loss Harvesting Opportunities Found:**\n\n${oppList}\n\n**Total Potential Tax Savings:** CHF ${(totalSavings / 1000).toFixed(1)}K\n\nI've opened the Tax-Efficient Positioning section with detailed recommendations.`,
        uiCommands: [
          { type: 'SCROLL_TO', payload: 'tax-efficiency' }
        ],
        data: { taxOpportunities: taxOps },
        suggestedQuestions: [
          "Tell me more about the first opportunity",
          "What's the wash sale risk?",
          "Show optimization suggestions"
        ]
      };
    }

    if (isOptimizationQuery) {
      const clientDataAny = this.clientData as any;
      const scenarios = clientDataAny.optimizationScenarios || [];
      const highPriority = scenarios.filter((s: any) => s.priority === 'HIGH');

      return {
        intent: 'FIND_OPPORTUNITIES',
        response: `**Portfolio Optimization Opportunities:**\n\nI found **${scenarios.length} optimization scenarios** (${highPriority.length} high priority):\n\n${scenarios.slice(0, 3).map((s: any, idx: number) =>
          `${idx + 1}. **${s.title}** [${s.priority}]\n   Impact: ${s.estimatedImpact.riskReduction ? `-${s.estimatedImpact.riskReduction}% risk` : ''}${s.estimatedImpact.returnImprovement ? ` +${s.estimatedImpact.returnImprovement}% return` : ''}`
        ).join('\n\n')}\n\nI've scrolled to the Optimization Suggestions section for details.`,
        uiCommands: [
          { type: 'SCROLL_TO', payload: 'optimization' }
        ],
        data: { scenarios },
        suggestedQuestions: [
          "Tell me about reducing MMGR concentration",
          "How should I deploy excess cash?",
          "What's the best way to rebalance?"
        ]
      };
    }

    return {
      intent: 'FIND_OPPORTUNITIES',
      response: "I can help you find tax optimization, rebalancing opportunities, or risk reduction strategies. What are you looking for?",
      suggestedQuestions: [
        "Find tax harvesting opportunities",
        "Find optimization opportunities",
        "Show me the best rebalancing strategy"
      ]
    };
  }

  /**
   * Handle: "Show top 3 risks and what to do"
   */
  private handleShowRisks(_query: string): QueryResult {
    const concentrationRisks = this.clientData.insights.concentrationRisks;

    if (concentrationRisks.length === 0) {
      return {
        intent: 'SHOW_RISKS',
        response: "**Great news!** No significant concentration risks detected. Your portfolio appears well-diversified.",
        suggestedQuestions: [
          "Show diversification score",
          "Compare portfolio correlations",
          "Find optimization opportunities"
        ]
      };
    }

    const topRisks = concentrationRisks.slice(0, 3);
    const riskList = topRisks.map((risk, idx) =>
      `**${idx + 1}. ${risk.title}** [${risk.severity}]\n   ${risk.description}\n   **Value:** CHF ${(risk.value / 1000).toFixed(0)}K (${risk.percentage.toFixed(1)}%)\n   **Action:** ${risk.recommendation}`
    ).join('\n\n');

    return {
      intent: 'SHOW_RISKS',
      response: `**Top ${topRisks.length} Concentration Risks:**\n\n${riskList}\n\nI've highlighted the risks in the Overview tab. Check the Optimization Suggestions for detailed action plans.`,
      uiCommands: [
        { type: 'SWITCH_TAB', payload: 'overview' },
        { type: 'HIGHLIGHT_SECTION', payload: 'concentration-risks' }
      ],
      data: { risks: topRisks },
      suggestedQuestions: [
        "How do I reduce MMGR concentration?",
        "Find optimization opportunities",
        "What if I sold half my largest position?"
      ]
    };
  }

  /**
   * Handle unknown queries
   */
  private handleUnknown(_query: string): QueryResult {
    return {
      intent: 'UNKNOWN',
      response: "I'm not sure I understood that. I can help you with:\n\n• Filtering positions (e.g., 'Show portfolios with Apple')\n• Comparing portfolios\n• Analyzing metrics (USD exposure, risks, performance)\n• What-if scenarios (e.g., 'What if I sold MMGR?')\n• Finding opportunities (tax harvesting, optimization)\n• Showing top risks\n\nWhat would you like to explore?",
      suggestedQuestions: [
        "Show me all portfolios that have Apple",
        "What's my USD exposure?",
        "Find tax harvesting opportunities",
        "What are my top 3 risks?"
      ]
    };
  }

  /**
   * Extract security name from query
   */
  private extractSecurityName(query: string): string | null {
    // Simple extraction - look for common position names
    const positions = this.clientData.consolidatedHoldings.positions;

    for (const position of positions) {
      const nameParts = position.name.toLowerCase().split(' ');
      for (const part of nameParts) {
        if (query.includes(part) && part.length > 3) {
          return position.name;
        }
      }
    }

    // Check for ticker symbols or common abbreviations
    if (query.includes('aapl') || query.includes('apple')) return 'Apple Inc';
    if (query.includes('mmgr')) return 'MMGR Fund';
    if (query.includes('nestle') || query.includes('nestlé')) return 'Nestlé SA';
    if (query.includes('ubs')) return 'UBS Switzerland AG';
    if (query.includes('roche')) return 'Roche Holding AG';

    return null;
  }
}

export default AIQueryEngine;
